import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Paperclip } from 'lucide-react'
import { findActiveMention, findActiveFileMention, normalizeMentionToken } from '../../lib/mentionParser'

/**
 * Textarea with autocomplete for:
 *   @  -> company members (inserts `@token`)
 *   #  -> card files (inserts `#[filename]`)
 *
 * Members: {id, full_name, email?, avatar_url?}. Files: {id, name, ...}.
 * onChange receives raw text; consumers use extractMentionTokens() /
 * extractFileMentionNames() on submit/render.
 */
export default function MentionTextarea({
    value,
    onChange,
    onSubmit,
    members = [],
    files = [],
    placeholder = '',
    disabled = false,
    onPaste,
}) {
    const { t } = useTranslation()
    const inputRef = useRef(null)
    const dropdownRef = useRef(null)
    const activeKeyRef = useRef('')
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [active, setActive] = useState(null) // { kind:'user'|'file', query, start, end }
    const [highlighted, setHighlighted] = useState(0)

    const updateMention = () => {
        const el = inputRef.current
        if (!el) return
        const cursor = el.selectionStart ?? value.length
        const userM = findActiveMention(value, cursor)
        const fileM = userM ? null : findActiveFileMention(value, cursor)
        const next = userM ? { kind: 'user', ...userM } : (fileM ? { kind: 'file', ...fileM } : null)
        const nextKey = next ? `${next.kind}:${next.start}:${next.query}` : ''
        if (activeKeyRef.current !== nextKey) {
            activeKeyRef.current = nextKey
            setHighlighted(0)
        }
        setActive(next)
        setDropdownOpen(!!next)
    }

    useEffect(() => {
        updateMention()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: react to value only
    }, [value])

    useEffect(() => {
        const el = inputRef.current
        if (!el) return
        el.style.height = 'auto'
        el.style.height = `${Math.min(el.scrollHeight, 132)}px`
    }, [value])

    const matches = !active
        ? []
        : active.kind === 'user'
            ? members
                .filter((m) => {
                    const query = normalizeMentionToken(active.query)
                    if (!query) return true
                    const label = normalizeMentionToken(m.full_name || m.email || '')
                    const email = normalizeMentionToken(m.email || '')
                    return label.includes(query) || email.includes(query)
                })
            : files
                .filter((f) => {
                    const query = active.query.trim().toLowerCase()
                    if (!query) return true
                    return (f.name || '').toLowerCase().includes(query)
                })

    useEffect(() => {
        if (!dropdownOpen) return
        dropdownRef.current
            ?.querySelector(`[data-mention-index="${highlighted}"]`)
            ?.scrollIntoView({ block: 'nearest' })
    }, [dropdownOpen, highlighted])

    const insert = (item) => {
        if (!active) return
        const before = value.slice(0, active.start)
        const after = value.slice(active.end)
        const inserted = active.kind === 'user'
            ? `@${normalizeMentionToken(item.full_name || item.email || '')} `
            : `#[${item.name}] `
        const next = `${before}${inserted}${after}`
        onChange(next)
        setDropdownOpen(false)
        setActive(null)
        activeKeyRef.current = ''
        setTimeout(() => {
            const el = inputRef.current
            if (el) {
                el.focus()
                const pos = before.length + inserted.length
                el.setSelectionRange(pos, pos)
            }
        }, 0)
    }

    const handleKeyDown = (e) => {
        if (dropdownOpen && matches.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((h) => (h + 1) % matches.length); return }
            if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((h) => (h - 1 + matches.length) % matches.length); return }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insert(matches[highlighted]); return }
            if (e.key === 'Escape') { e.preventDefault(); setDropdownOpen(false); return }
        }
        if (e.key === 'Enter' && e.shiftKey) return
        if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
            e.preventDefault()
            onSubmit()
        }
    }

    return (
        <div className="mention-textarea">
            <textarea
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onKeyUp={(event) => {
                    if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(event.key)) updateMention()
                }}
                onClick={updateMention}
                onPaste={onPaste}
                rows={1}
                placeholder={placeholder || t('suggestions.writeCommentPlaceholder')}
                disabled={disabled}
            />
            {dropdownOpen && matches.length > 0 && (
                <div className="mention-dropdown" ref={dropdownRef}>
                    {matches.map((item, idx) => (
                        <button
                            type="button"
                            key={item.id}
                            data-mention-index={idx}
                            className={`mention-item ${idx === highlighted ? 'highlighted' : ''}`.trim()}
                            onMouseDown={(e) => { e.preventDefault(); insert(item) }}
                            onMouseEnter={() => setHighlighted(idx)}
                        >
                            {active?.kind === 'file' ? (
                                <>
                                    <span className="mention-file-icon"><Paperclip size={13} /></span>
                                    <span className="mention-name">{item.name}</span>
                                </>
                            ) : (
                                <>
                                    <span
                                        className="mention-avatar"
                                        style={{ background: `hsl(${(item.full_name?.length || 0) * 37 % 360}, 50%, 40%)` }}
                                    >
                                        {(item.full_name || item.email || '?').charAt(0).toUpperCase()}
                                    </span>
                                    <span className="mention-name">{item.full_name || item.email || t('sharedMemberPicker.userFallback')}</span>
                                </>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
